import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Crown, Check, HelpCircle, X, Loader2, ArrowLeft, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

const SAMPLE_VERDICTS = [
  { verdict: 'GO', label: 'Plain Greek yogurt', reason: 'Simple ingredients, supports your goal.' },
  { verdict: 'MAYBE', label: 'Granola bar', reason: 'Added sugar — fine occasionally.' },
  { verdict: 'HELL NO', label: 'Neon sports drink', reason: 'Artificial dyes + high sugar load.' }
];

const SAMPLE_ICON = { GO: Check, MAYBE: HelpCircle, 'HELL NO': X };
const SAMPLE_TONE = {
  GO: { accent: 'text-qc-green', bg: 'bg-qc-green/10', border: 'border-qc-green/30' },
  MAYBE: { accent: 'text-qc-amber', bg: 'bg-qc-amber/10', border: 'border-qc-amber/30' },
  'HELL NO': { accent: 'text-qc-red', bg: 'bg-qc-red/10', border: 'border-qc-red/30' }
};

export default function Upgrade() {
  const navigate = useNavigate();
  const { token, loadProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    setCheckingPayment(true);
    toast.info('Checking payment status...');
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh.');
      setCheckingPayment(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/payments/checkout/status/${sessionId}`, {
        withCredentials: true
      });
      if (response.data.payment_status === 'paid') {
        toast.success('Welcome to Pro!');
        await loadProfile();
        setTimeout(() => navigate('/home'), 1500);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      toast.error('Error checking payment status.');
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API_URL}/payments/checkout/session`,
        { package_id: packageId, origin_url: originUrl },
        { withCredentials: true }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-qc-bg text-qc-text"
      data-testid="upgrade-screen"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
    >
      {/* Header */}
      <div className="px-5 py-3 flex items-center max-w-lg mx-auto">
        <button
          onClick={() => navigate('/home')}
          data-testid="back-button"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text tappable"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-extrabold text-qc-text ml-3">Upgrade</h1>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-4 pb-12">
        {/* Hero */}
        <div className="mb-6">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-qc-text leading-tight mb-3" data-testid="paywall-headline">
            Know in seconds.<br />
            <span className="text-qc-green">Shop with confidence.</span>
          </h1>
          <p className="text-base text-qc-text-dim">
            Get clear GO / MAYBE / HELL NO verdicts on every grocery decision.
          </p>
        </div>

        {/* 3 Sample Verdicts */}
        <div className="space-y-2 mb-6" data-testid="sample-verdicts">
          {SAMPLE_VERDICTS.map((s) => {
            const Icon = SAMPLE_ICON[s.verdict];
            const tone = SAMPLE_TONE[s.verdict];
            return (
              <div key={s.verdict} className={`flex items-center gap-3 p-4 rounded-2xl border ${tone.border} ${tone.bg}`}>
                <div className={`w-12 h-12 rounded-full ${tone.bg} border ${tone.border} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-6 h-6 ${tone.accent}`} strokeWidth={3} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-base font-black ${tone.accent} tracking-tight`}>{s.verdict}</span>
                    <span className="text-sm font-bold text-qc-text truncate">{s.label}</span>
                  </div>
                  <p className="text-sm text-qc-text-dim leading-snug mt-0.5">{s.reason}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Social proof */}
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="social-proof">
          <div className="flex -space-x-2">
            <div className="w-7 h-7 rounded-full bg-qc-green border-2 border-qc-bg" />
            <div className="w-7 h-7 rounded-full bg-qc-amber border-2 border-qc-bg" />
            <div className="w-7 h-7 rounded-full bg-qc-red border-2 border-qc-bg" />
            <div className="w-7 h-7 rounded-full bg-qc-surface2 border-2 border-qc-bg flex items-center justify-center text-[10px] font-bold text-qc-text">+</div>
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-3.5 h-3.5 text-qc-amber fill-qc-amber" />
            ))}
          </div>
          <p className="text-sm text-qc-text-dim font-medium">
            Join <span className="text-qc-text font-bold">12,500+</span> shopping smarter
          </p>
        </div>

        {/* Pro Plan Card */}
        <div
          className="bg-qc-surface rounded-3xl border-2 border-qc-green p-6 mb-4 relative overflow-hidden"
          data-testid="upgrade-card"
        >
          <span className="absolute top-4 right-4 bg-qc-green text-qc-bg text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider">
            Best value
          </span>
          <h2 className="text-2xl font-extrabold text-qc-text mb-1">Pro</h2>
          <p className="text-sm text-qc-text-dim mb-4">Less than a coffee per month.</p>

          <div className="flex items-baseline gap-2 mb-5">
            <span className="text-5xl font-black text-qc-green tracking-tight">$4.99</span>
            <span className="text-sm font-bold text-qc-text-dim">/month</span>
          </div>

          <div className="space-y-3 mb-6">
            {[
              'Unlimited scans',
              'Full scan history with thumbnails',
              'Detailed reasons + what to buy instead',
              'Priority verdicts on new ingredients'
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-qc-green flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-qc-bg" strokeWidth={3} />
                </div>
                <p className="text-base text-qc-text">{feature}</p>
              </div>
            ))}
          </div>

          <Button
            onClick={() => handleUpgrade('pro_monthly')}
            disabled={loading || checkingPayment}
            data-testid="upgrade-to-pro-button"
            className="capture-btn w-full h-14 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-2xl font-extrabold text-base shadow-[0_8px_24px_rgba(0,230,118,0.35)]"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...
              </>
            ) : (
              <>
                <Crown className="w-5 h-5 mr-2" /> Upgrade to Pro
              </>
            )}
          </Button>
        </div>

        {/* Free trial - prominent */}
        <div className="bg-qc-surface rounded-2xl border border-qc-border p-5 mb-5" data-testid="free-trial-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-qc-amber/15 border border-qc-amber/40 flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-qc-amber" />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-qc-text">Start free — no credit card required</h3>
              <p className="text-sm text-qc-text-dim mt-0.5">7 scans on us. See if it clicks.</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/home')}
            data-testid="start-free-button"
            variant="outline"
            className="w-full h-12 bg-qc-surface2 border-qc-border text-qc-text hover:bg-qc-border rounded-xl font-bold"
          >
            Start free
          </Button>
        </div>

        {/* Trial Option */}
        <div className="text-center mb-2">
          <Button
            onClick={() => handleUpgrade('trial_3day')}
            disabled={loading || checkingPayment}
            data-testid="trial-button"
            variant="ghost"
            className="text-qc-text-dim hover:text-qc-text font-bold"
          >
            Try 3-day Pro pass for $0.99
          </Button>
        </div>

        {/* Trial auto-renewal disclosure */}
        <p className="text-[11px] text-center text-qc-text-faint mb-3 px-2" data-testid="trial-disclosure">
          Trial auto-renews to $4.99/month after 3 days unless cancelled. Cancel anytime in account settings.
        </p>

        <p className="text-xs text-center text-qc-text-faint">
          Cancel anytime. No commitments.
        </p>
      </div>
    </div>
  );
}
